import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { motion, AnimatePresence } from "framer-motion";

const GA_ID = "G-FNYWC884Z3";

function getConsent(): string | null {
  try {
    return localStorage.getItem("cookie_consent");
  } catch {
    return null;
  }
}

function setConsent(value: "accepted" | "refused") {
  try {
    localStorage.setItem("cookie_consent", value);
  } catch {
    // silent
  }
}

function loadGA() {
  if (document.getElementById("ga-script")) return;
  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID);
}

function removeGA() {
  // Disable GA and remove cookies
  (window as any)[`ga-disable-${GA_ID}`] = true;
  document.cookie.split(";").forEach((c) => {
    const name = c.trim().split("=")[0] ?? "";
    if (name.startsWith("_ga")) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === "accepted") {
      loadGA();
    } else if (consent === "refused") {
      removeGA();
    } else {
      // No choice yet — show banner
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    setConsent("accepted");
    loadGA();
    setVisible(false);
  };

  const handleRefuse = () => {
    setConsent("refused");
    removeGA();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ x: -20, opacity: 0, scale: 0.95 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: -20, opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className="fixed bottom-4 left-4 z-50 max-w-xs"
        >
          <div className="bg-card border border-border rounded-lg shadow-lg p-4">
            <p className="font-body text-xs leading-relaxed text-foreground mb-3">
              🍪 On utilise des cookies pour mieux comprendre comment vous naviguez.{" "}
              <Link to="/politique-de-confidentialite" className="underline underline-offset-2 text-accent hover:text-accent/80">
                En savoir plus
              </Link>
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleRefuse}
                className="flex-1 px-3 py-1.5 text-[10px] font-body uppercase tracking-wider border border-border rounded-md hover:bg-muted transition-colors text-muted-foreground"
              >
                Non merci
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-3 py-1.5 text-[10px] font-body uppercase tracking-wider bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
              >
                Ok pour moi !
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;